// A pool of Web Workers that play games for the class training, one worker per spare core.
// Games are handed out in small chunks so fast and slow cores finish a generation together.
import type { MatchResult } from '../shared/engine';
import type { RunGames, SimGame } from '../shared/classtrain';

interface Job { id: number; games: SimGame[]; resolve: (r: MatchResult[]) => void; reject: (e: Error) => void }

export class SimPool {
  readonly size: number;
  private idle: Worker[] = [];
  private all: Worker[] = [];
  private queue: Job[] = [];
  private pending = new Map<number, Job>();
  private nextId = 1;

  constructor(size = Math.max(1, (navigator.hardwareConcurrency || 4) - 1)) {
    this.size = size;
    for (let i = 0; i < size; i++) {
      const w = new Worker('/simworker.js');
      w.onmessage = (e: MessageEvent<{ id: number; ok: boolean; out?: MatchResult[]; error?: string }>) => {
        const job = this.pending.get(e.data.id);
        this.pending.delete(e.data.id);
        this.idle.push(w);
        if (job) { if (e.data.ok) job.resolve(e.data.out!); else job.reject(new Error(e.data.error)); }
        this.pump();
      };
      w.onerror = (e) => { // a worker that failed to load or crashed: fail the whole run instead of hanging
        e.preventDefault();
        const err = new Error('模拟线程出错了：' + (e.message || '没能启动'));
        for (const job of [...this.pending.values(), ...this.queue]) job.reject(err);
        this.pending.clear();
        this.queue = [];
      };
      this.all.push(w);
      this.idle.push(w);
    }
  }

  run: RunGames = (games, chunk = 8) => {
    const parts: Promise<MatchResult[]>[] = [];
    for (let i = 0; i < games.length; i += chunk) {
      const slice = games.slice(i, i + chunk);
      parts.push(new Promise((resolve, reject) => this.queue.push({ id: this.nextId++, games: slice, resolve, reject })));
    }
    this.pump();
    return Promise.all(parts).then((xs) => xs.flat());
  };

  private pump() {
    while (this.idle.length && this.queue.length) {
      const job = this.queue.shift()!;
      const w = this.idle.pop()!;
      this.pending.set(job.id, job);
      w.postMessage({ id: job.id, games: job.games });
    }
  }

  destroy() { for (const w of this.all) w.terminate(); }
}
